import Image from "next/image";
import Quote from "@/components/Quote";
export const covidSchoolHubContent = (
  <>
    <div className="prose dark:prose-invert max-w-none text-text-muted-light dark:text-text-muted-dark">
      <p>
        Dr. Emily Oster—author, economics professor at Brown University, and
        Rhode Island resident—is big on research and data, and has (written) the
        books to prove it. When she took to Twitter, seeking a partner to help
        create a blog resource on COVID-19, Township reached out, helping
        publish data around masking and sanitizing in the early stages of the
        pandemic. But the pronounced data gap around the educational impacts of
        COVID prompted a brand new project for the partners: a school data hub.
      </p>

      <p>
        To better understand the varied consequences of school closures, Dr.
        Oster and her team began to gather data about educational modes employed
        in each state due to COVID-19, including:
      </p>

      <ul>
        <li>
          <p>Learning model (e.g., virtual, hybrid, in-person)</p>
        </li>
        <li>
          <p>Enrollment by learning model</p>
        </li>
        <li>
          <p>COVID-19 case counts among students and/or staff</p>
        </li>
      </ul>

      <Image
        src="https://images.ctfassets.net/iu0ut201adbf/2RnXRo6d4YhlJ8HEQxYgm4/fca3c435a7a4bd6e8e0247150f4f8009/covidhub-fullbleed-1.webp"
        alt="COVID School Hub Banner"
        width={1200}
        height={600}
        className="w-full h-auto my-8"
      />

      <h2>The Challenge</h2>
      <p>
        The U.S. contains approximately 13,800 public school districts, all of
        which did virtual, hybrid, and in-person schooling at varying times. Dr.
        Oster&rsquo;s team collected and analyzed the mountain of data
        submitted; but they also sought a meaningful way to share it with the
        educators, researchers, and policymakers who would determine learning
        and health decisions for future school years.
      </p>
      <p>
        Both Township and Dr. Oster&rsquo;s team saw immeasurable value in
        providing a free resource to the larger community.
      </p>

      <h2>Approach</h2>
      <p>
        At the outset of the project, Township collaborated and closely
        communicated with Dr. Oster&rsquo;s team to show school learning models
        from 2020-21. Turning data into something meaningful meant making it
        come to life in an engaging way through visualizations. Together, they
        sought to make school learning model data available for every state, but
        Township also assessed the feasibility of an even more ambitious goal:
        to visualize every piece of district data over time, in a single place.
      </p>
      <p>
        Township had taken on similar projects, but not yet at this scale.
        Rendering 13,800 items on a homepage was a technological feat. With a
        massive spreadsheet regularly updated by Dr. Oster&rsquo;s research
        team, updates were always coming in. With so much information flowing
        through the site, resizing data files and rendering maps quickly were
        vital to keeping the site user-friendly with best possible page load
        time.
      </p>

      <p>
        Township was able to find data sources that allowed SVG map renderings
        of every U.S. school district. On top of this, the goal was to have
        users select dates throughout the early days of the pandemic and compare
        maps over time. They found a way, but there was one problem: SVG works
        well for vector graphics like maps, but rendering a complex SVG (with
        thousands of districts) can cause slow load times. To improve these
        large-scale SVG map renderings, Township sought a solution.
      </p>
      <p>
        After a bit of digging, they found it: they offloaded the SVG map
        rendering to a separate server. This involved converting the SVG maps
        into static PNG images, which were then transmitted back to the original
        public-facing site. The change resulted in a significant reduction in
        load times, dropping from around 10 seconds to 1 second or less.
      </p>

      <Image
        src="https://images.ctfassets.net/iu0ut201adbf/3xCQzU6zrcKvsNR7fCZgee/fa5c27c42b6aedd8278beca1c639fecf/covidhub-fullbleed-2.webp"
        alt="COVID School Hub Banner"
        width={1200}
        height={600}
        className="w-full h-auto my-8"
      />

      <h3>Insight</h3>
      <h2>
        Dr. Oster&rsquo;s books, reports, and studies are based on resources and
        statistics—and there weren&rsquo;t enough to create meaningful claims
        about COVID-19 impacts on education.
      </h2>

      <p>
        Township rose to the occasion, taking large amounts of disparate data
        from Dr. Oster&rsquo;s team and overcoming numerous technical challenges
        to visually present it online; through the project they were able to
        refine a data visualization skillset that navigates complexity for
        impact. Since there were zero off-the-shelf options to provide what was
        required, Township combined best practices to create a final data
        rendering with a time-based slider interaction at a massive scale.
      </p>
      <p>
        Both Township and Dr. Oster&rsquo;s team sought to carve out a path that
        would help society at large, putting time and effort into a bigger
        picture, with positive impact—with every step based on plenty of
        research.
      </p>

      <h2>Solution</h2>
      <p>
        Dr. Oster came to Township to create a source that &quot;[would] be
        widely used for any work in which researchers hope to understand the
        landscape of pandemic schooling.&quot; Township helped bring that data
        to the public in a digestible way, as a comprehensive, accessible base
        of data on the intersection of COVID-19, education, and health in
        schools during the 2020-21 school year.
      </p>
      <p>
        At the beginning, 20 states had submitted data for the project. Today,
        44 of 50 states are represented in the data visualization. Creating a
        user-friendly site that visually represents the data for two years of
        education impacts has become an invaluable resource for research and
        future decisions.
      </p>
      <p>
        Throughout the partnership, trust was intrinsic—Dr. Oster handed over
        the data, guided Township in what to build, and allowed Township to
        further push the bounds of complex data presentation with a
        user-friendly,{" "}
        <a href="https://www.covidschooldatahub.com/">informative site</a>.
      </p>
      <p>
        Read more about the COVID-19 School Data Hub in this{" "}
        <a href="https://assets.ctfassets.net/9fbw4onh0qc1/59ZMlGVC2fkTG4fBCBpneI/516d69b386a8e86858aff5714bcb277e/Press_Release__Dr._Emily_Oster_Launches_the_Most_Comprehensive_Base_of_Data_on_COVID-19_and_Schools.pdf">
          press release
        </a>{" "}
        from Dr. Oster.
      </p>

      <Quote
        text="Township has been fantastic to work with; they are responsive and capable problem solvers, and willing to adapt as the project has adapted."
        author="Dr. Emily Oster"
        role="Author and Professor of Economics at Brown University"
        type="client"
        className="md:-mx-[10%] md:w-[120%]"
        image={{
          src: "/assets/logos/oster.webp",
          alt: "Dr. Emily Oster",
        }}
      />

      <h2>The Future</h2>
      <p>
        The two-year time frame of displayed data will make it possible for
        researchers to answer crucial questions about pandemic schooling and its
        far-reaching impacts. Township&rsquo;s team was equal parts proud and
        excited to collaborate with Dr. Oster and her team to fill a big data
        gap beautifully.
      </p>
    </div>
  </>
);
